import { type VariantProps } from "class-variance-authority";
import { LucideIcon } from "lucide-react";
import * as React from "react";
import { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Button } from "./button";
import {
  emptyStateBodyVariants,
  emptyStateDescriptionVariants,
  emptyStateIconVariants,
  emptyStateIconWrapperVariants,
  emptyStateTitleVariants,
  emptyStateVariants,
} from "./empty-state-variants";

import { cn } from "@/lib/utils/cn";

type EmptyStateProps = VariantProps<typeof emptyStateVariants> & {
  icon: LucideIcon;
  /** Tints the icon — e.g. `text-primary` when the empty state is actionable rather than terminal. */
  iconClassName?: string;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick?: (event: React.MouseEvent) => void;
    href?: string;
    icon?: ReactNode;
  };
};

function EmptyStateAction({
  action,
  layout,
}: Required<Pick<EmptyStateProps, "action">> &
  Pick<EmptyStateProps, "layout">) {
  /* The inline variant sits inside click-to-navigate cards, where an outline button reads as a
     second card-level control. A text link keeps the affordance subordinate to the card. */
  if (layout === "inline" && action.href) {
    return (
      <Link
        to={action.href}
        onClick={action.onClick}
        className="mt-1 inline-block text-xs font-medium text-primary underline-offset-2 hover:underline"
      >
        {action.icon}
        {action.label} →
      </Link>
    );
  }

  return (
    <Button
      variant="outline"
      className="mt-2"
      onClick={action.onClick}
      asChild={!!action.href}
    >
      {action.href ? (
        <Link to={action.href}>
          {action.icon}
          {action.label}
        </Link>
      ) : (
        <>
          {action.icon}
          {action.label}
        </>
      )}
    </Button>
  );
}

function EmptyState({
  icon: Icon,
  iconClassName,
  title,
  description,
  action,
  layout,
}: EmptyStateProps) {
  return (
    <div data-slot="empty-state" data-layout={layout ?? "centered"} className={emptyStateVariants({ layout })}>
      <div className={emptyStateIconWrapperVariants({ layout })}>
        <Icon className={cn(emptyStateIconVariants({ layout }), iconClassName)} />
      </div>
      <div className={emptyStateBodyVariants({ layout })}>
        <p className={emptyStateTitleVariants({ layout })}>{title}</p>
        {description && (
          <p className={emptyStateDescriptionVariants({ layout })}>
            {description}
          </p>
        )}
        {layout === "inline" && action && (
          <EmptyStateAction action={action} layout={layout} />
        )}
      </div>
      {layout !== "inline" && action && (
        <EmptyStateAction action={action} layout={layout} />
      )}
    </div>
  );
}

export default EmptyState;
